import { Box, Typography } from '@mui/material'
import AdxInput from 'components/custom/AdxInput'
import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import React, { useEffect, useRef, useState } from 'react'
import { useMutation } from 'react-query'
import { handleValidationErrors } from 'services/helper/handleValidationErrors'
import Bugsnag from '@bugsnag/js'
import { Formik, Form } from 'formik'
import ACExternalEvalCsvUpload from '../components/ACExternalEvalCsvUpload'
import {
  addExternalEval,
  addExternalEvalUpload,
  confirmExternalEvalUpload,
} from 'services/api/modules/performance/appraisal/appraisal'
import AdxButton from 'components/custom/AdxButton'

const UploadExternalEvaluation = ({
  appraisalId,
  setGetExternalData,
  completeUploadExternal,
  setCompleteUploadExternal,
  setExternalCSVUpload,
  getExternalData,
  refetchAppraisalData,
  setExternalLoading,
}) => {
  const [openModel, setOpenModel] = useState(true)
  const [titleInput, setTitleInput] = useState()
  const [desc, setDesc] = useState()
  const [scoreLabel, setScoreLabel] = useState()
  const [maxScore, setMaxScore] = useState()
  const [enableUpload, setEnableUpload] = useState(false)

  const formikRef = useRef()

  const handleCloseCSV = () => {
    setOpenModel(false)
    setCompleteUploadExternal(false)
  }

  const handleTitleInput = (event) => {
    if (event.target.value.length > 0) {
      setTitleInput(event.target.value)
    } else {
      setTitleInput()
    }
  }

  const handleDescInput = (event) => {
    if (event.target.value.length > 0) {
      setDesc(event.target.value)
    } else {
      setDesc()
    }
  }

  const handleMaxScoreLabelInput = (event) => {
    if (event.target.value.length > 0) {
      setScoreLabel(event.target.value)
    } else {
      setScoreLabel()
    }
  }

  const handleMaxScoreInput = (event) => {
    if (event.target.value.length > 0) {
      setMaxScore(event.target.value)
    } else {
      setMaxScore()
    }
  }

  const handleConfirm = (state) => {
    setCompleteUploadExternal(state)
  }

  const handleUpload = () => {
    mutateCreateExternal(externalData)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let externalData = {
    title: titleInput,
    full_score: maxScore,
    description: desc,
    full_score_label: scoreLabel,
    appraisal_id: appraisalId,
    weightage: 0,
  }

  useEffect(() => {
    const allValuesExist = Object.keys(externalData).every(
      (key) => externalData.hasOwnProperty(key) && externalData[key] !== undefined
    )

    if (allValuesExist) {
      setEnableUpload(true)
    } else {
      setEnableUpload(false)
    }
  }, [externalData])

  const handleUploadEval = async (values) => {
    let newData = values.import_evaluation_file[0]
    let formData = new FormData()
    formData.append('import_evaluation_file', newData)
    mutateUploadCSV({ data: formData })
  }

  const { mutate: mutateUploadCSV, isLoading: uploadCSV } = useMutation(addExternalEvalUpload, {
    onSuccess: (data) => {
      setGetExternalData(data.data)
      setCompleteUploadExternal(true)
      setExternalCSVUpload(true)
    },
    onError: (error) => {
      if (error.status === 422) {
        if (error.data?.errors) {
          handleValidationErrors(error, formikRef)
        } else {
          let errors = {
            import_evaluation_file: [error.data.message],
          }
          let newError = {
            data: {
              errors,
            },
          }
          handleValidationErrors(newError, formikRef)
        }
      } else {
        Bugsnag.notify(error.data)
        let errors = {
          import_evaluation_file: [error.data.message],
        }
        let newError = {
          data: {
            errors,
          },
        }
        handleValidationErrors(newError, formikRef)
      }
    },
  })

  useEffect(() => {
    setExternalLoading(uploadCSV)
  }, [uploadCSV])

  const { mutate: mutateCreateExternal } = useMutation(addExternalEval, {
    onSuccess: async (response) => {
      mutateConfirmCreate({ data: getExternalData, evalId: response.data.id })
    },
    onError: (error) => {
      if (error.status === 422) {
        if (error.data?.errors) {
          handleValidationErrors(error, formikRef)
        } else {
          let errors = {
            import_evaluation_file: [error.data.message],
          }
          let newError = {
            data: {
              errors,
            },
          }
          handleValidationErrors(newError, formikRef)
        }
      } else {
        Bugsnag.notify(error.data)
        let errors = {
          import_evaluation_file: [error.data.message],
        }
        let newError = {
          data: {
            errors,
          },
        }
        handleValidationErrors(newError, formikRef)
      }
    },
  })

  const { mutate: mutateConfirmCreate } = useMutation(confirmExternalEvalUpload, {
    onSuccess: async () => {
      setCompleteUploadExternal(false)
      setOpenModel(false)
      refetchAppraisalData()
    },
    onError: (error) => {
      if (error.status === 422) {
        if (error.data?.errors) {
          handleValidationErrors(error, formikRef)
        } else {
          let errors = {
            import_evaluation_file: [error.data.message],
          }
          let newError = {
            data: {
              errors,
            },
          }
          handleValidationErrors(newError, formikRef)
        }
      } else {
        Bugsnag.notify(error.data)
        let errors = {
          import_evaluation_file: [error.data.message],
        }
        let newError = {
          data: {
            errors,
          },
        }
        handleValidationErrors(newError, formikRef)
      }
    },
  })

  return (
    <>
      <AdxFormModal
        open={openModel}
        onClose={handleCloseCSV}
        handleClose={handleCloseCSV}
        disableAutoFocus={true}
        sxModal={{ width: '800px' }}
        title={'Upload External Evaluation'}
        subtitle={'Only one type of external evaluation per upload'}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Textfield for Evaluation Name */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body1" mb={1}>
              Evaluation Name
            </Typography>
            <AdxInput
              type="standard"
              placeholder="Max character 50"
              charLimit={50}
              onChange={handleTitleInput}
            />
          </Box>
          {/* Textfield for Description */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body1" mb={1}>
              Description
            </Typography>
            <AdxInput
              size="standard"
              placeholder="Max character 500"
              charLimit={500}
              onChange={handleDescInput}
              multiline
              sxInput={{ maxHeight: '200px', overflowY: 'auto' }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Textfield for Score Name */}
            <AdxInput
              type="standard"
              placeholder="Enter the name of this score"
              label="Score Label"
              onChange={handleMaxScoreLabelInput}
            />
            {/* Textfield for Score Max Value */}
            <AdxInput
              type="standard"
              placeholder="Enter the maximum score for this evaluation"
              label="Maximum Score"
              onChange={handleMaxScoreInput}
            />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body1">Evaluation Data</Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                border: 1,
                borderColor: 'gray-line.main',
                borderRadius: '4.5px',
                p: 2,
              }}
            >
              <Formik
                enableReinitialize
                initialValues={{
                  import_evaluation_file: null,
                }}
                onSubmit={handleUploadEval}
                innerRef={formikRef}
              >
                {(formikProps) => (
                  <>
                    <Form height="300px">
                      <ACExternalEvalCsvUpload
                        formikProps={formikProps}
                        handleConfirm={handleConfirm}
                        enableUpload={enableUpload}
                      />
                    </Form>
                  </>
                )}
              </Formik>
            </Box>
          </Box>

          {completeUploadExternal && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: '10px' }}>
              <AdxButton variant={'contained'} sx={{ width: '35%' }} onClick={handleUpload}>
                Upload
              </AdxButton>
            </Box>
          )}
        </Box>
      </AdxFormModal>
    </>
  )
}

export default UploadExternalEvaluation
